import clsx from 'clsx';
import React, { useCallback } from 'react';
import { FiClipboard, FiInfo } from 'react-icons/fi';
import { toast } from 'react-toastify';

import { Icon } from '@/components/icon/icon';
import { Tooltip } from '@/components/tooltip/tooltip';

import styles from './propertyItem.module.scss';
import { getPropertyTestId } from './propertyTestId';

type PropertyItemProps = {
  label: string;
  value: string | number | React.ReactNode;
  variant?: 'default' | 'large';
  helperText?: string;
  testId?: string;
  copyable?: boolean;
  labelTooltip?: string;
  action?: React.ReactNode;
};

export const PropertyItem: React.FC<PropertyItemProps> = ( { label, value, variant = 'default', helperText, testId, copyable = false, labelTooltip, action } ) => {
  const generatedTestId = testId || getPropertyTestId( label );

  const copyToClipboard = useCallback( async() => {
    try {
      await navigator.clipboard.writeText( String( value ) );
      toast.success( 'Copied to clipboard!' );
    } catch { /** no op */ }
  }, [ value ] );

  if ( variant === 'large' ) {
    return (
      <div className={ styles.propertyLarge } data-testid={ generatedTestId }>
        <div className={ styles.propertyLargeHeader }>
          <div className={ styles.propertyLabel } data-testid={ `${ generatedTestId }-label` }>
            { label }
            {labelTooltip && (
              <Tooltip content={ labelTooltip } position="right">
                <span className={ styles.labelInfoIcon }><FiInfo /></span>
              </Tooltip>
            )}
          </div>
          { action && <div className={ styles.propertyAction }>{ action }</div> }
        </div>
        <div className={ styles.propertyValue } data-testid={ `${ generatedTestId }-value` }>{ value }</div>
        {helperText && <div className={ styles.propertyHelper }>{ helperText }</div>}
      </div>
    );
  }

  if ( copyable ) {
    return (
      <div className={ clsx( styles.property, styles.propertyCopyable ) } data-testid={ generatedTestId }>
        <div className={ styles.propertyHeader }>
          <strong data-testid={ `${ generatedTestId }-label` }>{ label }:</strong>
          {labelTooltip && (
            <Tooltip content={ labelTooltip } position="left">
              <span className={ styles.labelInfoIcon } data-testid={ `${ generatedTestId }-info` }><FiInfo /></span>
            </Tooltip>
          )}
        </div>
        <div className={ styles.copyContainer }>
          <Tooltip content="Copy to clipboard" position="top">
            <button
              type="button"
              onClick={ copyToClipboard }
              className={ styles.copyButton }
              aria-label="Copy to clipboard"
              data-testid={ `${ generatedTestId }-copy` }
            >
              <Icon icon={ FiClipboard } />
            </button>
          </Tooltip>
          <span data-testid={ `${ generatedTestId }-value` }>{ value }</span>
        </div>
      </div>
    );
  }

  return (
    <div className={ styles.property } data-testid={ generatedTestId }>
      <strong data-testid={ `${ generatedTestId }-label` }>
        { label }:
        {labelTooltip && (
          <Tooltip content={ labelTooltip } position="right">
            <span className={ styles.labelInfoIcon } data-testid={ `${ generatedTestId }-info` }><FiInfo /></span>
          </Tooltip>
        )}
      </strong>
      <span data-testid={ `${ generatedTestId }-value` }>{ value }</span>
    </div>
  );
};
